import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { ContactModal } from "@/components/contact-modal";
import { PlanetMark } from "@/components/planet-mark";
import { Twitter, Linkedin, Github } from "lucide-react";

export function SiteFooter() {
  const [contactOpen, setContactOpen] = useState(false);
  const year = new Date().getFullYear();

  const col = (title: string, items: Array<{ label: string; to?: string; onClick?: () => void; href?: string }>) => (
    <div>
      <h4 className="text-xs font-medium uppercase tracking-[0.14em] text-foreground/80">{title}</h4>
      <ul className="mt-4 space-y-2.5">
        {items.map((it) => (
          <li key={it.label}>
            {it.to ? (
              <Link to={it.to} className="text-sm text-muted-foreground transition-colors hover:text-foreground">
                {it.label}
              </Link>
            ) : it.href ? (
              <a href={it.href} className="text-sm text-muted-foreground transition-colors hover:text-foreground">
                {it.label}
              </a>
            ) : (
              <button onClick={it.onClick} className="text-left text-sm text-muted-foreground transition-colors hover:text-foreground">
                {it.label}
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <footer className="mt-32 border-t border-border/50 pt-14 pb-10">
      <div className="mx-auto max-w-6xl px-6">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-5">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-2">
              <PlanetMark className="h-6 w-6" />
              <span className="text-base font-semibold tracking-tight">Pyng</span>
            </Link>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-muted-foreground">
              A private space for the relationships that matter. Calm, intelligent, yours.
            </p>
            <div className="mt-5 flex items-center gap-2">
              <a href="#" aria-label="Twitter" className="grid h-8 w-8 place-items-center rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30">
                <Twitter className="h-3.5 w-3.5" />
              </a>
              <a href="#" aria-label="LinkedIn" className="grid h-8 w-8 place-items-center rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30">
                <Linkedin className="h-3.5 w-3.5" />
              </a>
              <a href="#" aria-label="GitHub" className="grid h-8 w-8 place-items-center rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30">
                <Github className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>

          {col("Product", [
            { label: "How it works", href: "/#magic-moment" },
            { label: "Pricing", to: "/pricing" },
            { label: "Privacy-first", href: "/#privacy" },
          ])}

          {col("Company", [
            { label: "About", to: "/about" },
            { label: "Contact", onClick: () => setContactOpen(true) },
            { label: "FAQ", to: "/faq" },
          ])}

          {col("Legal", [
            { label: "Privacy", to: "/privacy" },
            { label: "Security", to: "/security" },
            { label: "Subprocessors", to: "/subprocessors" },
            { label: "Terms", to: "/terms" },
            { label: "Cookies", to: "/cookies" },
          ])}
        </div>

        <div className="mt-12 flex flex-col items-start justify-between gap-4 border-t border-border/50 pt-6 text-xs text-muted-foreground sm:flex-row sm:items-center">
          <p>© {year} Pyng. Made with care for the people you don't want to forget.</p>
          <p className="text-muted-foreground/70">Private beta · v0.1</p>
        </div>
      </div>
      <ContactModal open={contactOpen} onOpenChange={setContactOpen} />
    </footer>
  );
}
